// AppointmentBooking.tsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import {
  AppointmentData,
  Service,
  Location,
  AvailableSlot,
  AppointmentRequest,
} from "./types";
import ProfileHeader from "./ProfileHeader";
import ServicesSection from "./ServicesSection";
import LocationSection from "./LocationSection";
import BookingSection from "./BookingSection";
import SummaryCard from "./SummaryCard";
import ConfirmationModal from "./ConfirmationModal";
import MobileSummaryBar from "./MobileSummaryBar";
import { HeaderLanding } from "../HeaderLanding";
import ProfessionalNotFoundError from "./ProfessionalNotFoundError";
import "../../style/css/base.css";
import "../../style/css/AppointmentBooking.css";
import { useAuth } from "../../AuthContext";

const AppointmentBooking: React.FC = () => {
  // 1. Base Data State (Profile, Services, Locations)
  const [appointmentData, setAppointmentData] =
    useState<AppointmentData | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { username } = useParams<{ username: string }>();

  // 2. Selection State
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(
    null,
  );

  // 3. Calendar State (Lifted Up)
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());

  // 4. Slots Data State (Fetched dynamically)
  const [availableSlots, setAvailableSlots] = useState<AvailableSlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);

  const { user } = useAuth();
  const navigate = useNavigate();

  // Check if current user is viewing their own booking page
  const isViewingOwnPage = user && user.username === username;

  // --- EFFECT 1: Check mobile and load profile ---
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoadingProfile(true);
        const response = await fetch(`/api/public_profile/${username}/`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        });

        if (!response.ok) throw new Error("Error loading profile");

        const data: AppointmentData = await response.json();
        setAppointmentData(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Error desconocido");
      } finally {
        setLoadingProfile(false);
      }
    };
    fetchProfile();
  }, [username]);

  // --- EFFECT 2: Load Slots (Runs when criteria changes) ---
  useEffect(() => {
    // We only fetch slots if we have a service and location selected
    if (!selectedService || !selectedLocation) return;

    const fetchSlots = async () => {
      try {
        setLoadingSlots(true);
        setAvailableSlots([]); // Clear old slots while loading

        // Reset selection if the month changed to avoid stale dates
        setSelectedDate(null);
        setSelectedTime(null);

        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth() + 1; // JS months are 0-11

        const queryParams = new URLSearchParams({
          service_id: selectedService.id,
          location_id: selectedLocation.id,
          year: year.toString(),
          month: month.toString(),
          username: username || "",
        });

        const response = await fetch(`/api/slots/?${queryParams}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        });

        if (response.ok) {
          const data: AvailableSlot[] = await response.json();
          setAvailableSlots(data);
        }
      } catch (error) {
      } finally {
        setLoadingSlots(false);
      }
    };

    fetchSlots();
  }, [selectedService, selectedLocation, currentMonth, username]);

  const handleConfirmAppointment = (customerData: any) => {
    // Handle appointment confirmation logic
  };

  const handleGoBack = () => {
    window.history.back();
  };

  const handleRetry = () => {};

  const handleTryFree = () => {
    navigate("/#register");
  };

  if (loadingProfile) return <div className="loading">Cargando perfil...</div>;
  if (error)
    return (
      <ProfessionalNotFoundError
        message="No encontramos profesionales que coincidan con tu búsqueda."
        searchTerm={username}
        onRetry={handleRetry}
        onGoBack={handleGoBack}
      />
    );
  if (!appointmentData) return null;

  return (
    <div>
      {!user && (
        <HeaderLanding onTryFree={handleTryFree} showLoginButton={true} />
      )}

      <main>
        <div className="container">
          <section id="hero">
            <ProfileHeader professional={appointmentData.professional} />

            <div className="content-layout">
              <div>
                <ServicesSection
                  services={appointmentData.services}
                  selectedService={selectedService}
                  onSelectService={setSelectedService}
                />

                <LocationSection
                  locations={appointmentData.locations}
                  selectedLocation={selectedLocation}
                  onSelectLocation={setSelectedLocation}
                />

                <BookingSection
                  availableSlots={availableSlots}
                  selectedDate={selectedDate}
                  selectedTime={selectedTime}
                  onSelectDate={setSelectedDate}
                  onSelectTime={setSelectedTime}
                  currentMonth={currentMonth}
                  onMonthChange={setCurrentMonth}
                  isLoadingSlots={loadingSlots}
                />

                {/* Show message if user is viewing their own page */}
                {isViewingOwnPage && (
                  <div className="own-page-message">
                    <div className="message-card">
                      <svg
                        width="48"
                        height="48"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"
                          stroke="var(--color-accent)"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M12 8v4M12 16h.01"
                          stroke="var(--color-accent)"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <h3>Esta es tu página de reservas</h3>
                      <p>
                        Los pacientes verán esta página para agendar citas
                        contigo. Si deseas ver tus citas existentes o modificar
                        tu configuración, por favor accede a tu panel de
                        control.
                      </p>
                      <button
                        onClick={() => navigate("/dashboard")}
                        className="btn btn--primary"
                      >
                        Ir a mi panel de control
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Hide SummaryCard if user is viewing their own page */}
              {!isViewingOwnPage && (
                <aside>
                  <SummaryCard
                    professional={appointmentData.professional}
                    selectedService={selectedService}
                    selectedLocation={selectedLocation}
                    selectedDate={selectedDate}
                    selectedTime={selectedTime}
                    onConfirm={() => setShowModal(true)}
                  />
                </aside>
              )}
            </div>
          </section>
        </div>
      </main>

      {/* Hide MobileSummaryBar if user is viewing their own page */}
      {!isViewingOwnPage && (
        <MobileSummaryBar
          professional={appointmentData.professional}
          selectedService={selectedService}
          selectedLocation={selectedLocation}
          selectedDate={selectedDate}
          selectedTime={selectedTime}
          onConfirm={() => setShowModal(true)}
        />
      )}

      {/* Hide ConfirmationModal if user is viewing their own page */}
      {showModal && !isViewingOwnPage && (
        <ConfirmationModal
          professional={appointmentData.professional}
          selectedService={selectedService}
          selectedLocation={selectedLocation}
          selectedDate={selectedDate}
          selectedTime={selectedTime}
          onConfirm={handleConfirmAppointment}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

export default AppointmentBooking;
